import type { AbVariant } from '@/wabunka-src/common/core/abVariant/mapOptimizeNextPattern';
import { mapOptimizeNextPattern } from '@/wabunka-src/common/core/abVariant/mapOptimizeNextPattern';

/**
 * A/B バリアント確定の「結果（variant）」と「GA 割当イベントを送信すべきか」を一括で決める純粋ロジック。
 *
 * Vue/Nuxt に一切依存しない（core/ 配下）。`useAbVariant` のポーリング/ライフサイクルから切り出し、
 * 計測の送信/非送信判定（spec「バリアント割当の計測送信」MUST/MUST NOT）を Vitest で網羅できるようにする。
 *
 * 確定のきっかけ（source）ごとに、GA を送るのは **Optimize Next の実割当（0/1+）が確定したときのみ**。
 *
 * - `assignment`（実割当ポーリング）: `mapOptimizeNextPattern` が control/variant を返したら送信、null（未割当）→ control・送信なし
 * - `override`（`?ab=` 強制 / 非本番のみ）: 指定 variant・送信なし（実割当ではないため）
 * - `preview`（Optimize Next プレビュー）: 指定 variant・送信なし（QA 用途のため）
 * - `experimentOff`（experiment ID 空文字）: control・送信なし
 * - `timeout`（最大待機超過）: control・送信なし
 */
export type AbResolutionSource =
  | { type: 'assignment'; pattern: number | undefined }
  | { type: 'override'; variant: AbVariant }
  | { type: 'preview'; variant: AbVariant }
  | { type: 'experimentOff' }
  | { type: 'timeout' };

export interface AbDecision {
  variant: AbVariant;
  shouldSendGa: boolean;
}

export const resolveAbDecision = (source: AbResolutionSource): AbDecision => {
  switch (source.type) {
    case 'assignment': {
      const mapped = mapOptimizeNextPattern(source.pattern);
      // null（undefined/負値/NaN 等の未割当）→ control にフォールバックし送信しない
      if (mapped === null) return { variant: 'control', shouldSendGa: false };
      // 0/1+ の実割当が確定した場合のみ GA を送信する
      return { variant: mapped, shouldSendGa: true };
    }
    case 'override':
    case 'preview':
      return { variant: source.variant, shouldSendGa: false };
    case 'experimentOff':
    case 'timeout':
      return { variant: 'control', shouldSendGa: false };
  }
};
